/**
 * Utility for generating a PST (America/Los_Angeles) date/time suffix
 * to be appended to all CSV export filenames.
 *
 * Format: _M-D-YYYY_h-m-s
 *   - M  = month (1–12, no leading zero)
 *   - D  = day (1–31, no leading zero)
 *   - YYYY = 4-digit year
 *   - h  = hour in 12-hour format (1–12, no leading zero)
 *   - m  = minute (0–59, no leading zero)
 *   - s  = second (0–59, no leading zero)
 *
 * Example: _5-5-2026_9-7-3
 */

const PST_TIMEZONE = "America/Los_Angeles";

/**
 * Returns the current date/time parts in the PST timezone.
 * Exposed separately to allow deterministic testing via injection.
 *
 * @param now - Optional Date to use instead of the real current time.
 * @returns An object containing the individual date/time components in PST.
 */
export const getPstDateTimeParts = (
  now: Date = new Date(),
): { month: number; day: number; year: number; hour: number; minute: number; second: number } => {
  // Use Intl.DateTimeFormat to extract each component in the PST timezone.
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: PST_TIMEZONE,
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: true,
  });

  const parts = formatter.formatToParts(now);

  const get = (type: string): number => {
    const found = parts.find((p) => p.type === type);
    return found ? Number.parseInt(found.value, 10) : 0;
  };

  return {
    month: get("month"),
    day: get("day"),
    year: get("year"),
    hour: get("hour"),
    minute: get("minute"),
    second: get("second"),
  };
};

/**
 * Returns a PST date/time suffix string in the format `_M-D-YYYY_h-m-s`.
 * This suffix is intended to be appended to a CSV base filename before the
 * `.csv` extension so that every downloaded file is uniquely timestamped.
 *
 * @param now - Optional Date override for deterministic testing.
 * @returns Suffix string, e.g. `_5-5-2026_9-7-3`.
 */
export const getPstDateTimeSuffix = (now?: Date): string => {
  const { month, day, year, hour, minute, second } = getPstDateTimeParts(now);
  return `_${month}-${day}-${year}-${hour}-${minute}-${second}`;
};

/**
 * Injects the PST date/time suffix into a CSV filename before the `.csv`
 * extension.  If the filename does not end with `.csv` (case-insensitive),
 * the suffix is appended at the end.
 *
 * @param fileName - The resolved base filename, e.g. `TEST_OF_CHANGE.csv`.
 * @param now - Optional Date override for deterministic testing.
 * @returns Timestamped filename, e.g. `TEST_OF_CHANGE_5-5-2026_9-7-3.csv`.
 */
export const injectPstSuffixIntoFilename = (fileName: string, now?: Date): string => {
  const suffix = getPstDateTimeSuffix(now);
  const csvExtensionPattern = /\.csv$/i;

  if (csvExtensionPattern.test(fileName)) {
    return fileName.replace(csvExtensionPattern, `${suffix}.csv`);
  }

  return `${fileName}${suffix}`;
};
